import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  assetsApi,
  MATCH_LEVEL_LABEL,
  REPLACEMENT_KIND_LABEL,
  type AssetDetail,
  type AssetSummary,
  type MountCandidate,
  type MountRequest,
  type ReplacementKind,
} from "../api/assets";
import { structureApi, type PositionDetail, type PositionNode } from "../api/structure";
import type { ApiError } from "../api/client";
import { detailsMap, formatApiError } from "../lib/apiError";
import { isoDatePlusDays, localDateTimeInputToIso, toLocalDateTimeInput } from "../lib/dates";
import { pathLabel } from "../lib/paths";
import { scannedAssetCode } from "../lib/assetCode";
import { usePositionKinds } from "../lib/usePositionKinds";
import { Modal } from "./Modal";
import { PositionTreePicker } from "./PositionTreePicker";
import { QrScanner } from "./QrScannerLazy";
import { ConditionChip } from "./ConditionChip";
import { DateInput, DateTimeInput } from "./DateInput";
import Ic from "./icons.jsx";

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

/** Default reminder for a temporary replacement (contract §3: today + 14 days). */
export const TEMPORARY_REMINDER_DAYS = 14;

type Target = {
  id: string;
  name: string;
  sequenceNo: number | null;
  kind: PositionNode["kind"];
  expectedTypeId: string | null;
  expectedTypeName: string | null;
  mountedAssetTag: string | null;
  pathLabel: string;
};

type Mismatch = { expectedTypeName: string | null };

type Props = {
  onClose: () => void;
  onMounted: (detail: AssetDetail) => void;
} & (
  /** Known asset → pick the target position in the tree; `mode: "remount"` moves a MOUNTED asset to another position. */
  | { asset: AssetSummary; position?: undefined; mode?: "mount" | "remount" }
  /** Known (empty) position → pick the asset from mount candidates or by QR. */
  | { position: PositionDetail; asset?: undefined }
);

/**
 * Mount dialog (contract §3 `POST /assets/{id}/mount`, §7). Two entry modes
 * share the final step (permanent / temporary, reminder, when, note). A 409
 * TYPE_MISMATCH from the server — or a visible type mismatch with the target
 * — shows a warning with the "Ipak montiraj" checkbox that resends with
 * `force: true`.
 */
export function MountModal(props: Props) {
  const { t } = useTranslation();
  const { labelOf } = usePositionKinds();
  const { onClose, onMounted } = props;
  const remount = props.asset !== undefined && props.mode === "remount";

  // ---- what & where ------------------------------------------------------
  const [treeTarget, setTreeTarget] = useState<PositionNode | null>(null);
  const [candidate, setCandidate] = useState<AssetSummary | null>(null);
  const asset: AssetSummary | null = props.asset ?? candidate;
  const target: Target | null = useMemo(() => {
    if (props.position) {
      const p = props.position;
      return {
        id: p.id, name: p.name, sequenceNo: p.sequenceNo, kind: p.kind,
        expectedTypeId: p.expectedTypeId, expectedTypeName: p.expectedTypeName,
        mountedAssetTag: p.mountedAsset?.tag ?? null,
        pathLabel: pathLabel(p.path, positionTitle(p)),
      };
    }
    if (!treeTarget) return null;
    return {
      id: treeTarget.id, name: treeTarget.name, sequenceNo: treeTarget.sequenceNo, kind: treeTarget.kind,
      expectedTypeId: treeTarget.expectedTypeId, expectedTypeName: treeTarget.expectedTypeName,
      mountedAssetTag: treeTarget.mountedAssetTag,
      pathLabel: positionTitle(treeTarget),
    };
  }, [props.position, treeTarget]);

  // ---- common step -------------------------------------------------------
  const [replacementKind, setReplacementKind] = useState<ReplacementKind>("PERMANENT");
  const [reminderAt, setReminderAt] = useState(() => isoDatePlusDays(TEMPORARY_REMINDER_DAYS));
  const [occurredAt, setOccurredAt] = useState(() => toLocalDateTimeInput());
  const [note, setNote] = useState("");
  const [serverMismatch, setServerMismatch] = useState<Mismatch | null>(null);
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A visible type mismatch is flagged before the round-trip; the server is
  // still the authority (409 TYPE_MISMATCH).
  const clientMismatch: Mismatch | null =
    asset && target && target.expectedTypeId && target.expectedTypeId !== asset.typeId
      ? { expectedTypeName: target.expectedTypeName }
      : null;
  const mismatch = serverMismatch ?? clientMismatch;

  // Changing the pair resets the warning/force decision.
  useEffect(() => { setServerMismatch(null); setForce(false); setError(null); }, [asset?.id, target?.id]);

  const occupied = !!target?.mountedAssetTag;
  const samePosition = remount && !!target && target.id === props.asset?.positionId;
  const canSubmit = !!asset && !!target && !occupied && !samePosition && (!mismatch || force) && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!asset || !target || !canSubmit) return;
    setError(null);
    const body: MountRequest = {
      positionId: target.id,
      replacementKind,
      replacementReminderAt: replacementKind === "TEMPORARY" ? (reminderAt || null) : null,
      occurredAt: localDateTimeInputToIso(occurredAt),
      note: note.trim() || null,
      ...(force ? { force: true } : {}),
    };
    setSubmitting(true);
    try {
      onMounted(await (remount ? assetsApi.remount(asset.id, body) : assetsApi.mount(asset.id, body)));
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "TYPE_MISMATCH") {
        const d = detailsMap(ae);
        setServerMismatch({ expectedTypeName: d?.expectedTypeName ?? null });
      } else {
        setError(translateMountError(t, ae, labelOf(target.kind)));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const title = remount ? t("remount.title", "Premeštanje asseta") : t("mount.title", "Montaža asseta");
  const subtitle = remount && props.asset
    ? t("remount.subAsset", "Asset {{tag}} je na poziciji {{position}} — izaberite novu poziciju u drvetu.", { tag: props.asset.tag, position: props.asset.positionName ?? "" })
    : props.asset
      ? t("mount.subAsset", "Asset {{tag}} — izaberite poziciju u drvetu.", { tag: props.asset.tag })
      : t("mount.subPosition", "Pozicija: {{name}}", { name: target?.pathLabel ?? "" });

  return (
    <Modal title={title} subtitle={subtitle} onClose={onClose} width={680}>
      <form onSubmit={handleSubmit} className="mount-form" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {props.asset ? (
          <div className="field">
            <span>{t("mount.target", "Ciljna pozicija")} <span className="req-mark">*</span></span>
            <PositionTreePicker
              value={treeTarget?.id ?? null}
              onChange={setTreeTarget}
              disabledIds={remount && props.asset?.positionId ? new Set([props.asset.positionId]) : undefined}
            />
          </div>
        ) : (
          <CandidatePicker position={props.position} value={candidate} onChange={setCandidate} />
        )}

        {target && (
          <div className="mount-target-info">
            <div><b>{target.pathLabel}</b></div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              {target.expectedTypeName
                ? t("mount.expectedType", "Očekivani tip: {{name}}", { name: target.expectedTypeName })
                : t("mount.expectedNone", "Bez očekivanog tipa — montaža bilo kog asseta.")}
            </div>
            {occupied && (
              <div style={errorBox} role="alert">
                {t("mount.error.occupied", "Pozicija je zauzeta — montiran je {{tag}}. Prvo ga demontirajte.", { tag: target.mountedAssetTag })}
              </div>
            )}
          </div>
        )}

        {mismatch && !occupied && (
          <div className="note-intervention" role="alert">
            <div className="src"><Ic.alert /> {t("mount.mismatch.title", "Tip ne odgovara očekivanom")}</div>
            <div>
              {t("mount.mismatch.type", "Pozicija očekuje tip „{{expected}}“, a asset je „{{actual}}“.", { expected: mismatch.expectedTypeName ?? "", actual: asset?.typeName ?? "" })}
            </div>
            <label className="check-row" style={{ marginTop: 6 }}>
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
              {t("mount.mismatch.force", "Ipak montiraj")}
            </label>
          </div>
        )}

        <div className="field">
          <span>{t("mount.kind", "Vrsta zamene")}</span>
          <div className="chip-group mount-kind" role="radiogroup" aria-label={t("mount.kind", "Vrsta zamene")}>
            {(["PERMANENT", "TEMPORARY"] as const).map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={replacementKind === k}
                className={`chip${replacementKind === k ? " active" : ""}`}
                onClick={() => setReplacementKind(k)}
              >
                {t(`assets.replacementKind.${k}`, REPLACEMENT_KIND_LABEL[k])}
              </button>
            ))}
          </div>
        </div>

        <div className="form-grid">
          {replacementKind === "TEMPORARY" && (
            <label className="field">
              <span>{t("mount.reminderAt", "Podsetnik (datum)")}</span>
              <DateInput value={reminderAt} onChange={setReminderAt} />
              <span className="muted" style={{ fontSize: 12 }}>{t("mount.reminderHint", "Podrazumevano danas + 14 dana.")}</span>
            </label>
          )}
          <label className="field">
            <span>{t("assets.field.occurredAt", "Datum i vreme")}</span>
            <DateTimeInput value={occurredAt} max={toLocalDateTimeInput()} onChange={setOccurredAt} />
          </label>
        </div>

        <label className="field">
          <span>{t("assets.field.note", "Napomena")}</span>
          <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} />
        </label>

        {error && <div style={errorBox} role="alert">{error}</div>}

        <div className="form-actions">
          <button type="button" className="btn lg" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary lg" disabled={!canSubmit}>
            {submitting ? t("action.saving", "Čuvanje…") : remount ? t("remount.submit", "Premesti") : t("mount.submit", "Montiraj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/** Mount-candidate list for a known position: search (debounced), QR scan, match-level labels. */
function CandidatePicker({
  position, value, onChange,
}: {
  position: PositionDetail;
  value: AssetSummary | null;
  onChange: (asset: AssetSummary | null) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [items, setItems] = useState<MountCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanner, setScanner] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    structureApi.mountCandidates(position.id, debouncedQ || undefined)
      .then((list) => { if (!cancelled) setItems(list); })
      .catch((err: ApiError) => { if (!cancelled) { setError(err.message); setItems([]); } });
    return () => { cancelled = true; };
  }, [position.id, debouncedQ]);

  async function onScanned(text: string) {
    setScanner(false);
    setScanError(null);
    const tag = scannedAssetCode(text);
    try {
      const detail = await assetsApi.byTag(tag);
      if (detail.usage === "MONTIRAN") {
        setScanError(t("mount.scan.alreadyMounted", "Asset {{tag}} je već montiran ({{pos}}).", { tag: detail.tag, pos: detail.positionName ?? "" }));
      } else if (detail.condition !== "ISPRAVAN") {
        setScanError(t("mount.scan.notOperational", "Asset {{tag}} nije ispravan — ne može se montirati.", { tag: detail.tag }));
      } else {
        onChange(detail);
      }
    } catch (err) {
      const ae = err as ApiError;
      setScanError(ae.status === 404
        ? t("assets.byTag.notFound", "Asset sa oznakom {{tag}} ne postoji.", { tag })
        : ae.message);
    }
  }

  if (value) {
    return (
      <div className="field">
        <span>{t("mount.asset", "Asset")}</span>
        <div className="mount-picked">
          <span className="asset-tag">{value.tag}</span>
          <span>{value.typeName}</span>
          <ConditionChip condition={value.condition} />
          <button type="button" className="btn sm" onClick={() => onChange(null)}>{t("mount.changeAsset", "Promeni")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="field">
      <span>{t("mount.pickAsset", "Izaberite asset")} <span className="req-mark">*</span></span>
      <div className="mount-search-row">
        <div className="search-wrap" style={{ position: "relative" }}>
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("mount.searchPlaceholder", "Oznaka, serijski, tip…")}
            aria-label={t("action.search", "Pretraga")}
          />
        </div>
        <button type="button" className="btn" onClick={() => setScanner(true)}>
          <Ic.selector /> {t("assets.scanQr", "Skeniraj QR")}
        </button>
      </div>
      {scanError && <div style={errorBox} role="alert">{scanError}</div>}
      {error && <div style={errorBox} role="alert">{error}</div>}
      <div className="candidate-list" role="listbox" aria-label={t("mount.pickAsset", "Izaberite asset")}>
        {items === null ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : items.length === 0 ? (
          <div className="empty">
            {debouncedQ
              ? t("mount.noCandidatesFiltered", "Nema ispravnih nemontiranih asseta za „{{q}}“.", { q: debouncedQ })
              : t("mount.noCandidates", "Nema ispravnih asseta očekivanog tipa na lageru — pretražite po oznaci za druge.")}
          </div>
        ) : (
          items.map((c) => (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={false}
              className="candidate-row"
              onClick={() => onChange(c)}
            >
              <span className="asset-tag">{c.tag}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span>{c.typeName}</span>
                {c.serialNo && <span className="muted mono" style={{ marginLeft: 6, fontSize: 12 }}>{c.serialNo}</span>}
                <span className="muted" style={{ display: "block", fontSize: 12 }}>
                  {c.effectiveLocation ? c.effectiveLocation.name : "—"}{c.locationNote ? ` · ${c.locationNote}` : ""}
                </span>
              </span>
              <span className={`tag ${c.matchLevel === "TYPE" ? "ok" : ""}`}>
                {t(`mount.match.${c.matchLevel}`, MATCH_LEVEL_LABEL[c.matchLevel])}
              </span>
            </button>
          ))
        )}
      </div>
      {scanner && <QrScanner onClose={() => setScanner(false)} onResult={(tag) => { void onScanned(tag); }} />}
    </div>
  );
}

function positionTitle(p: { name: string; sequenceNo: number | null }): string {
  return p.sequenceNo !== null ? `${p.name} #${p.sequenceNo}` : p.name;
}

/** `kindLabel` = display name of the target position's kind (409 KIND_NOT_HOLDING, 2026-09-11). */
export function translateMountError(t: Translate, ae: ApiError, kindLabel: string): string {
  switch (ae.code) {
    case "KIND_NOT_HOLDING": return t("mount.error.kindNotHolding", "Na poziciju vrste „{{kind}}“ se ne montira asset — vrsta ne nosi asset.", { kind: kindLabel });
    case "NOT_OPERATIONAL": return t("mount.error.notOperational", "Asset nije ispravan — montira se samo ispravan asset.");
    case "ALREADY_MOUNTED": return t("mount.error.alreadyMounted", "Asset je već montiran na drugu poziciju.");
    case "POSITION_OCCUPIED": return t("mount.error.occupied", "Pozicija je zauzeta — montiran je {{tag}}. Prvo ga demontirajte.", { tag: detailsMap(ae)?.mountedAssetTag ?? "?" });
    default: return formatApiError(ae);
  }
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
